import React, { useState } from "react";
import { Container, Box, Link, Typography, Grid, useMediaQuery, Collapse, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from "@mui/icons-material";

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isExpanded, setIsExpanded] = useState(false);

  // Mapping of display text to actual URL paths
  const linkMapping = {
    "About": "/legal/about",
    "Terms of Service": "/legal/terms-of-service", 
    "Privacy Policy": "/legal/privacy-policy",
    "Community Guidelines": "/legal/community-guidelines",
    "Cookie Policy": "/legal/cookie-policy"
  };

  const links = [
    "About",
    "Terms of Service",
    "Privacy Policy",
    "Community Guidelines",
    "Cookie Policy"
  ];

  // Option 1: Balanced 2x2 + 1 layout
  const renderBalancedLayout = () => {
    const firstRow = links.slice(0, 2);  // About, Terms
    const secondRow = links.slice(2, 4); // Privacy, Guidelines
    const thirdRow = links.slice(4);     // Cookie Policy

    return (
      <Box sx={{ pt: isMobile ? 2 : 3 }}> {/* 🔧 FIXED: Reduced mobile top padding */}
        {/* First row - 2 links */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          {firstRow.map((text) => (
            <Link
              key={text}
              href={linkMapping[text]}
              color="inherit"
              sx={{
                fontSize: "0.8rem",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
                px: 3,
                py: 1,
              }}
            >
              {text}
            </Link>
          ))}
        </Box>
        
        {/* Second row - 2 links */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          {secondRow.map((text) => (
            <Link
              key={text}
              href={linkMapping[text]}
              color="inherit"
              sx={{
                fontSize: "0.8rem",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
                px: 3,
                py: 1,
              }}
            >
              {text}
            </Link>
          ))}
        </Box>
        
        {/* Third row - 1 link centered */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {thirdRow.map((text) => (
            <Link
              key={text}
              href={linkMapping[text]}
              color="inherit"
              sx={{
                fontSize: "0.8rem",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
                px: 3,
                py: 1,
              }}
            >
              {text}
            </Link>
          ))}
        </Box>
      </Box>
    );
  };

  // Option 2: Vertical stack layout
  const renderVerticalLayout = () => (
    <Box sx={{ pt: isMobile ? 2 : 3 }}> {/* 🔧 FIXED: Reduced mobile top padding */}
      {links.map((text) => (
        <Box key={text} sx={{ mb: 1.5 }}>
          <Link
            href={linkMapping[text]}
            color="inherit"
            sx={{
              fontSize: "0.85rem",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
              display: "block",
              textAlign: "center",
              py: 0.5,
            }}
          >
            {text}
          </Link>
        </Box>
      ))}
    </Box>
  );

  // Option 3: Collapsible footer
  const renderCollapsibleLayout = () => (
    <Box sx={{ pt: isMobile ? 2 : 3 }}> {/* 🔧 FIXED: Reduced mobile top padding */}
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 2 }}>
        <Typography variant="body2" sx={{ fontSize: "0.85rem", mr: 1 }}>
          Legal & Info
        </Typography>
        <IconButton 
          size="small" 
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ p: 0.5 }}
        >
          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      
      <Collapse in={isExpanded}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
          {links.map((text) => (
            <Link
              key={text}
              href={linkMapping[text]}
              color="inherit"
              sx={{
                fontSize: "0.8rem",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
                py: 0.5,
              }}
            >
              {text}
            </Link>
          ))}
        </Box>
      </Collapse>
    </Box>
  );

  // Option 4: Compact grid (2 columns)
  const renderCompactGrid = () => (
    <Grid container spacing={1} justifyContent="center" sx={{ pt: isMobile ? 2 : 3, maxWidth: 300, mx: "auto" }}>
      {links.map((text) => (
        <Grid item xs={6} key={text}>
          <Link
            href={linkMapping[text]}
            color="inherit"
            sx={{
              fontSize: "0.75rem",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
              display: "block",
              textAlign: "center",
              py: 1,
              px: 0.5,
            }}
          >
            {text}
          </Link>
        </Grid>
      ))}
    </Grid>
  );

  // Option 5: Minimal - show only essential links
  const renderMinimalLayout = () => {
    const essentialLinks = ["About", "Privacy Policy", "Terms of Service"];
    
    return (
      <Box sx={{ pt: isMobile ? 2 : 3 }}> {/* 🔧 FIXED: Reduced mobile top padding */}
        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2 }}>
          {essentialLinks.map((text) => (
            <Link
              key={text}
              href={linkMapping[text]}
              color="inherit"
              sx={{
                fontSize: "0.8rem",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
                py: 1,
              }}
            >
              {text}
            </Link>
          ))}
        </Box>
        
        {/* Secondary links in smaller text */}
        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2, mt: 1 }}>
          {["Community Guidelines", "Cookie Policy"].map((text) => (
            <Link
              key={text}
              href={linkMapping[text]}
              color="text.secondary"
              sx={{
                fontSize: "0.7rem",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
                py: 0.5,
              }}
            >
              {text}
            </Link>
          ))}
        </Box>
      </Box>
    );
  };

  // Desktop layout (unchanged)
  const renderDesktopLayout = () => (
    <Grid container spacing={2} justifyContent="center" sx={{ pt: 3 }}>
      {links.map((text) => (
        <Grid item xs={6} sm={4} md="auto" key={text} sx={{ textAlign: "center" }}>
          <Link
            href={linkMapping[text]}
            color="inherit"
            sx={{
              fontSize: { xs: "0.8rem", md: "0.875rem" },
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {text}
          </Link>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: "#F8F9F9",
        padding: isMobile ? "10px 0" : "20px 0", // 🔧 FIXED: Reduced mobile padding
        pt: isMobile ? 0 : 5, // 🔧 FIXED: Much less top padding on mobile
        textAlign: "center",
        width: "100%",
        // 🔧 FIXED: Add margin bottom to account for bottom navigation on mobile
        mb: isMobile ? '60px' : 0,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ borderTop: "1px solid #efefef" }}>
          {/* You can switch between different mobile layouts here */}
          {isMobile ? (
            <>
              {/* Option 1: Balanced Layout */}
              {/*renderBalancedLayout()*/}
              
              {/* Option 2: Vertical Layout */}
              {/* {renderVerticalLayout()} */}
              
              {/* Option 3: Collapsible Layout */}
              {renderCollapsibleLayout()}
              
              {/* Option 4: Compact Grid */}
              {/* {renderCompactGrid()} */}
              
              {/* Option 5: Minimal Layout */}
              {/* {renderMinimalLayout()} */}
            </>
          ) : (
            renderDesktopLayout()
          )}
        </Box>
        
        {/* Logo and Copyright */}
        <Box
          sx={{
            mt: isMobile ? 2 : 3, // 🔧 FIXED: Reduced mobile top margin
            mb: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src="/assets/revfer/skyblue.svg"
            alt="Revfer Logo"
            style={{ width: "30px", height: "15px" }}
          />
          <Typography variant="body2" sx={{ ml: 1, fontSize: "0.75rem" }}>
            Revfer &copy; {new Date().getFullYear()}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;